import React, { useMemo, useCallback, useEffect } from 'react';
import { Machine, assign } from 'xstate';
import { useMachine } from '@xstate/react';
import styled from '@emotion/styled';
import { uniq } from 'lodash';

import SelectorGrid from '~/theseus/components/SelectorGrid';
import SectionToggle from '~/theseus/components/SectionToggle';
import Button from '~/theseus/components/Button';

import Field from '../../Field';

const Image = styled.img`
  width: 100%;
`;

const ImageSelectorContainer = styled.div`
 width: min-content;
 flex-grow: 1;
 text-align: start;
`;

const machine = Machine({
  id: 'context-card-image-selector',
  context: {
    selected: null,
    images: [],
  },
  initial: 'noImage',
  states: {
    noImage: {
      on: {
        'IMAGE.toggleModes': {
          target: 'selecting',
          actions: assign({ selected: ctx => ctx.images[0].id }),
        },
      },
    },
    selecting: {
      on: {
        'IMAGE.select': {
          actions: assign({ selected: (_, evt) => evt.data }),
        },
        'IMAGE.toggleModes': {
          target: 'noImage',
          actions: assign({ selected: null }),
        },
      },
    },
  },
});

const ImageSelector = (props) => {
  const { images, onChange } = props;

  const [{ matches, context }, send] = useMachine(machine, {
    context: {
      images,
    },
  });

  const { selected } = context;

  useEffect(() => {
    onChange(selected);
  }, [onChange, selected]);

  const toggleModes = useCallback(() => send('IMAGE.toggleModes'), [send]);
  const selectImage = useCallback(id => send({ type: 'IMAGE.select', data: id }), [send]);

  // Some properties have duplicate images, which would cause the Selector Grid to throw an error since it is expecting all Items to have unique ids. Filtering unique urls will ensure that an error is not thrown
  const imageItems = useMemo(() => (
    uniq(images).map(imageUrl => (
      <SelectorGrid.Item id={imageUrl} key={imageUrl}>
        <Image src={imageUrl} alt={imageUrl} />
      </SelectorGrid.Item>
    ))
  ), [images]);

  return (
    <Field htmlFor="image-selector" css={{ alignItems: 'flex-start' }}>
      <span className="label">Landscape Image</span>
      <ImageSelectorContainer>
        <SectionToggle enabledOn={matches('noImage')}>
          <Button variant="secondary" type="button" onClick={toggleModes}>
            Choose an Image
          </Button>
        </SectionToggle>
        <SectionToggle enabledOn={matches('selecting')}>
          <Button
            variant="plain"
            type="button"
            css={{ marginBottom: '1em' }}
            onClick={toggleModes}
          >
            Remove Image
          </Button>
          <SelectorGrid
            id="image-selector"
            idPrefix="image-selector"
            label="Image Selector"
            selected={selected}
            onChange={selectImage}
          >
            { imageItems }
          </SelectorGrid>
        </SectionToggle>
      </ImageSelectorContainer>
    </Field>

  );
};

export default ImageSelector;
